import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ReactComponent as AddIcon } from '@/assets/icons/ri-add-line.svg';
import { ReactComponent as EditIcon } from '@/assets/icons/ri-edit-2-line.svg';

import { ReactComponent as TrashIcon } from '@/assets/icons/trash.svg';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import BreadcrumbLayout from '@/components/common/CustomLayout/BreadcrumbLayout';
import Table from '@/components/common/Table';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import UserVenueSelect from '@/components/common/UserVenueSelect';
import useUserVenueSelectFilter from '@/hooks/useUserVenueSelectFilter';
import {
	TOTAL_OPTION_FITLER,
	OPTION_FILTER_STYLE,
	FILTER_KEYS,
	FILTER,
	HEADERS,
} from './constant';

import { deleteNews, getAllNews } from '@/store/news/action';
import { getUserVenues } from '@/store/user-info/action';

import useDebounce from '@/hooks/useDebounce';
import { RootState, useAppDispatch } from '@/store';
import {GENERAL_STATUS_STYLE} from "@/constant/status";

const SORT: TableSort = {
	type: 'CLIENT',
	handler: () => {},
	customSort: {
		TITLE: (v1, v2) => v2.title.localeCompare(v1.title),
	},
};

const renderHeaderSection = (setSearchNews, goToCreate) => (
	<div className="flex justify-between items-center gap-8 p-0">
		<p className="font-bold text-2xl text-black-primary">News</p>
		<div className="flex items-center gap-4">
			<Input
				useReactForm={false}
				name="searchNews"
				type="text"
				placeholder="Search for News"
				onChange={(e) => setSearchNews(e.target.value)}
			/>
			<div className="bg-grey-line-subtle h-6 w-1" />
			<Button
				type="submit"
				variant="primary"
				className="text-white"
				onClick={goToCreate}
			>
				<AddIcon />
				<p>News</p>
			</Button>
		</div>
	</div>
);

const OptionFilter = ({
	index,
	status,
	isActive,
	onClick,
	total,
}: OptionFilterParam) => {
	const { id, name } = status;
	const selectedStyle = OPTION_FILTER_STYLE[`${isActive}`];

	const rightBorderStyle =
		index === TOTAL_OPTION_FITLER - 1 ? '' : 'border-r-0';

	return (
		<div
			role="button"
			tabIndex={0}
			onKeyDown={() => onClick(id)}
			onClick={() => onClick(id)}
			className={`flex border border-grey-soft py-2 px-4 cursor-pointer ${rightBorderStyle} ${selectedStyle.button}`}
		>
			<span className="mr-2">{name}</span>
			<div className={`px-2 rounded-xl ${selectedStyle.pill}`}>{total}</div>
		</div>
	);
};

const renderFilterSection = ({
	summary,
	activeStatus,
	onClick,
	venueOptions,
	selectedVenue,
	handleChangeVenue
}: RenderFilterSectionParam) => (
	<div className="flex items-center border border-grey-soft border-b-0">
		<div className="flex flex-row p-4">
			{FILTER_KEYS.map((filterKey, index) => (
				<OptionFilter
					key={FILTER[filterKey].id}
					index={index}
					status={FILTER[filterKey]}
					isActive={activeStatus === FILTER[filterKey].id}
					onClick={onClick}
					total={summary ? Number(summary[filterKey.toLocaleLowerCase()]) : 0}
				/>
			))}
		</div>
		<div className="border-r border-r-stroke-subtle h-6 my-auto mr-4"/>
		<div>
			<UserVenueSelect
				name="venueId"
				id="venueId"
				placeholder="Select Venue"
				options={venueOptions}
				value={selectedVenue}
				onChange={handleChangeVenue}
			/>
		</div>
	</div>
);

const renderCustomComponent = ({ onEditClick, setSelectedId }) => ({
	TITLE: ({ image, title }: TitleSectionParam) => (
		<div className="flex flex-row">
			<img className="mr-2 max-h-[48px]" width={96} src={image} alt="" />
			<span className="font-semibold">{title}</span>
		</div>
	),
	STATUS: (status) => (
		<div className={`p-1 text-center font-medium rounded ${GENERAL_STATUS_STYLE[status]}`}>{status}</div>
	),
	ACTION: (id) => (
		<div className="flex">
			<Button
				className="border border-grey-line-subtle px-4 py-2 mr-2"
				type="button"
				variant="basic"
				onClick={() => onEditClick(id)}
			>
				<EditIcon />
				<span className="ml-2">Edit</span>
			</Button>
			<Button
				className="border border-grey-line-subtle px-4 py-2"
				type="button"
				variant="basic"
				onClick={() => setSelectedId(id)}
			>
				<TrashIcon />
			</Button>
		</div>
	),
});

const ActionButton = ({ onAgree, onCancel }) => (
	<div className="flex justify-center">
		<Button variant="primary" type={null} onClick={onAgree}>
			Yes, i’m Sure
		</Button>
		<Button variant="secondary" onClick={onCancel}>
			No, Cancel
		</Button>
	</div>
);

const News = () => {
	const dispatch = useAppDispatch();
	const {
		items: news,
		meta,
		summary,
	} = useSelector((state: RootState) => state.news.state);

	const [searchParams, setSearchParams] = useSearchParams();
	const [
		currentVenueId,
		selectedVenue,
		venueOptions,
		handleChangeVenue,
	] = useUserVenueSelectFilter()
	const searchNewsParam = searchParams.get('searchNews');
	const activeStatus = searchParams.get('status') || 'ALL';

	const [selectedId, setSelectedId] = useState(null);

	const showModal = selectedId !== null;

	const navigate = useNavigate();

	const goToCreate = () => {
		navigate('/dashboard/news/add');
	};

	const newsTableBody = news.map((v) => ({
		id: v.id,
		value: {
			TITLE: {
				image: v.thumbnail_link,
				title: v.title,
			},
			AUTHOR: v.user?.name,
			CATEGORY: v.category_code,
			VENUE: v.venue?.name,
			STATUS: v.status,
			ACTION: v.id,
		},
	})) as TableBodyData[];

	const onEditClick = (id) => {
		navigate(`/dashboard/news/${id}/edit`);
	};

	const body: TableBody = {
		data: newsTableBody,
		component: renderCustomComponent({ onEditClick, setSelectedId }),
	};

	const [searchNews, setSearchNews] = useState(searchNewsParam);

	const debounceSearchNews = useDebounce(searchNews, 500);

	useEffect(() => {
		if (debounceSearchNews) {
			setSearchParams({
				searchNews: debounceSearchNews,
				status: activeStatus,
				venueId: currentVenueId
			});
		}
	}, [debounceSearchNews]);

	useEffect(() => {
		dispatch(getUserVenues({}))
		const finalStatus =
			activeStatus === 'ALL' || !activeStatus ? '' : activeStatus;
		dispatch(getAllNews({ status: finalStatus, search: searchNews || '', venueId: currentVenueId || '' }));
	}, [searchParams]);

	const handleChangeStatus = (status: string) => {
		setSearchParams({
			searchNews: debounceSearchNews,
			status,
			venueId: currentVenueId
		});
	};

	const tableMeta: TableSummarizeMeta = {
		startItem: meta ? meta.page * meta.size + 1 : 0,
		endItem: meta ? meta.page * meta.size + meta.size : 0,
		totalItem: summary ? summary.all : 0,
		lastPage: summary ? summary.all / 10 : 0,
		pageNumber: 1,
	};

	return (
		<BreadcrumbLayout>
			<div className="flex flex-col justify-between gap-8 p-0">
				{renderHeaderSection(setSearchNews, goToCreate)}

				<div className="">
					{renderFilterSection({
						summary,
						activeStatus,
						onClick: handleChangeStatus,
						venueOptions,
						selectedVenue,
						handleChangeVenue
					})}
					<Table headers={HEADERS} body={body} sort={SORT} meta={tableMeta} />
					<ConfirmationModal
						actions={ActionButton({
							onAgree: () => {
								dispatch(deleteNews(selectedId));
								setSelectedId(null);
							},
							onCancel: () => setSelectedId(null),
						})}
						title="Are you sure want to delete this news?"
						subtitle="You will not be able to recover this news"
						show={showModal}
						onClose={() => setSelectedId(null)}
						rootId="portal-mid"
					/>
				</div>
			</div>
		</BreadcrumbLayout>
	);
};

export default News;
